/**
 * The interactions a thread should show as a THING, not as a tool row.
 *
 * Most of what an agent does is work: greps, reads, edits, a hundred lines of
 * output nobody wants. `ToolCallCard` is right about all of it — collapse it,
 * put it behind one disclosure, and let the prose carry the turn.
 *
 * A handful of calls are not work. They are the agent REACHING something that
 * outlives the turn: a document that now exists, a session that is now running,
 * a comment somebody is going to have to answer. Those are the things a person
 * scrolls back to find, and rendered as `create_document  {"title":"Plan"}` at
 * 0.7rem inside a collapsed run of eleven, they are unfindable. Worse, they are
 * indistinguishable from the greps, which is the actual complaint: the session
 * transcript does not show what the session DID.
 *
 * So a card is not decoration. It is the claim that this call produced something
 * addressable, and the card's job is to be the address.
 *
 * PURE, over the projection, for `threadRows`' reason: what counts as a card and
 * what its one line says are rules worth testing without rendering anything.
 *
 * THE ARGUMENTS ARE THE TRUTH, THE RESULT IS THE FALLBACK. A call's arguments
 * are structured and were written by the model; its result is a sentence written
 * for a model to read, and parsing English is how a card starts lying. The only
 * thing taken from the result is an id that could not exist before the call ran
 * — a created document's, a newly opened session's — and when that parse fails
 * the card degrades to an unclickable one rather than to a wrong link.
 */

import type { TranscriptMessage } from '@mod/core/model'
import type { ToolCall } from './threadRows'
import { argString } from './toolArgs'

/** What a card points AT. The three kinds are the three things in a project an
 *  agent can hand somebody: a document, a session, a comment. */
export type CardTarget = 'document' | 'session' | 'comment'

export interface ThreadCard {
  target: CardTarget
  /**
   * The past-tense verb, which is the whole of what the card says happened.
   * Rendered as-is, so it is written the way a person would say it.
   */
  verb: string
  /** What it happened TO: a title, a path, the first line of a comment. */
  label: string
  /**
   * The document to open, when there is one to open. Absent means the card is
   * still worth drawing and simply is not a link — a `create_document` whose
   * result never came back is exactly that, and it is honest about it.
   */
  documentId?: string
  /** The session to open, on a session card. */
  sessionId?: string
  /** A second line, when the call carried one worth reading (a comment's body,
   *  a message's text). Absent on most cards. */
  detail?: string
  /** True while the call has not come back. The card draws itself as pending
   *  rather than claiming a document exists that might not. */
  pending?: boolean
  /** RFC 0301 S9: the Automerge change an edit made, so the card can undo
   *  exactly it. Only a session's own write names one. */
  changeHash?: string
  /** On an agent's reply: the comment it answered, so the row can say whose
   *  (RFC 0324 3d). */
  replyTo?: string
}

/**
 * Strip the host's namespacing off a tool name.
 *
 * The same tool is `create_document` to a mod worker, `mcp__mod__create_document`
 * to Claude Code, and `mod__create_document` to a third harness. Matching the
 * bare name would light cards on one harness and show tool rows on the others,
 * for the same session, depending on who happened to answer the turn.
 */
export function bareToolName(name: string): string {
  return name.replace(/^mcp__[^_]+(?:_[^_]+)*?__/, '').replace(/^mod[._]/, '')
}

/** An Automerge document id as it appears in a result sentence. Base58, so no
 *  `0`, `O`, `I` or `l`, and long enough that no English word matches. */
const DOC_ID = /\b[1-9A-HJ-NP-Za-km-z]{16,}\b/

/** The change an edit made, as `update_document` and `patch_document` announce
 *  it (RFC 0301 S9). Automerge hashes are 64 hex characters. */
const CHANGE = /\(change ([0-9a-f]{64})\)/

function firstLine(value: unknown, max = 120): string {
  const text = argString(value)
  if (text === undefined) return ''
  const line = text.split('\n', 1)[0]?.trim() ?? ''
  return line.length > max ? `${line.slice(0, max - 1)}…` : line
}

/**
 * Through `argString`, because arguments off the log are ImmutableString
 * wrappers and `typeof === 'string'` is false for every one of them in the app.
 * Before this every edit card had no document id: it read "a document", could
 * not resolve a title and could not be opened, while every test passed.
 */
function str(args: unknown, key: string): string {
  if (!args || typeof args !== 'object') return ''
  return argString((args as Record<string, unknown>)[key]) ?? ''
}

/** The id announced by a result sentence, immediately after one of `prefixes`.
 *  Anchored on the verb rather than scanning for the first id-shaped run,
 *  because these sentences also carry ids the call did not create: a comment's
 *  result names every session its mentions woke. */
function announcedId(
  result: string | undefined,
  prefixes: string[],
): string | undefined {
  if (!result) return undefined
  for (const prefix of prefixes) {
    const at = result.indexOf(prefix)
    if (at < 0) continue
    const rest = result.slice(at + prefix.length)
    const match = DOC_ID.exec(rest)
    // Only if it is the NEXT thing in the sentence. A match forty characters
    // later belongs to a different clause.
    if (match && match.index <= 1) return match[0]
  }
  return undefined
}

/**
 * The card a tool call deserves, or null when it is ordinary work.
 *
 * Reads deliberately as a table, one line per tool, because it IS one: these are
 * the project tools (RFC 0263/0267/0292), a closed set defined in mod-core,
 * and a generic rule over argument names — the trick `summarizeToolArgs` uses,
 * correctly, for a one-line summary — cannot tell a document that now exists
 * from a document that was read. Reaching something is not a shape you can
 * detect; it is a fact about the tool.
 *
 * READS AND LISTS ARE NOT CARDS. `read_document`, `list_sessions`, `read_task`
 * produce nothing and reach nobody: carding them would put a box around the
 * agent orienting itself and bury the one call that mattered among six that did
 * not. The test is whether somebody else could find the thing afterwards.
 */
export function cardForCall(
  call: ToolCall,
  result?: { text: string; isError?: boolean },
): ThreadCard | null {
  // A call that FAILED is not a card. The card asserts a thing exists; an error
  // says it does not, and the tool row already reports errors properly.
  if (result?.isError) return null
  const pending = result === undefined
  const text = result?.text
  const args = call.arguments
  const name = bareToolName(call.name)

  switch (name) {
    case 'create_document': {
      const documentId = announcedId(text, ['Created document '])
      return {
        target: 'document',
        verb: 'Wrote',
        label: str(args, 'title') || str(args, 'path') || 'a document',
        ...(documentId ? { documentId } : {}),
        ...(pending ? { pending: true } : {}),
      }
    }
    case 'save_file': {
      const documentId = announcedId(text, [' as document '])
      const file = str(args, 'file').split(/[\\/]/).pop()
      return {
        target: 'document',
        verb: 'Saved',
        label: str(args, 'title') || str(args, 'path') || file || 'a file',
        ...(documentId ? { documentId } : {}),
        ...(pending ? { pending: true } : {}),
      }
    }
    case 'update_document':
    case 'patch_document': {
      const documentId = str(args, 'documentId')
      // A change hash is an id that could not exist before the call ran, the
      // one kind of thing this file takes from a result.
      const changeHash = text ? CHANGE.exec(text)?.[1] : undefined
      // RFC 0338: update_document with only a path is a move, and says so.
      const given = (args ?? {}) as Record<string, unknown>
      const movedOnly =
        name === 'update_document' &&
        !!str(args, 'path') &&
        given.content === undefined &&
        given.data === undefined
      return {
        target: 'document',
        verb: movedOnly ? 'Moved' : 'Edited',
        label:
          str(args, 'title') || str(args, 'path') || documentId || 'a document',
        ...(documentId ? { documentId } : {}),
        ...(documentId && changeHash ? { changeHash } : {}),
        ...(pending ? { pending: true } : {}),
      }
    }
    case 'delete_document': {
      // No link, on purpose: the document is gone, and a card that offers to
      // open it is a broken pane one click away.
      return {
        target: 'document',
        verb: 'Deleted',
        label: str(args, 'documentId') || 'a document',
        ...(pending ? { pending: true } : {}),
      }
    }
    case 'move_folder': {
      // A folder is not a document, so there is nothing to link to.
      const from = str(args, 'from')
      const to = str(args, 'to')
      return {
        target: 'document',
        verb: 'Moved',
        label: from && to ? `${from}/ to ${to}/` : 'a folder',
        ...(pending ? { pending: true } : {}),
      }
    }
    case 'start_session': {
      // A plain child is "opened"; an RFC 0302 child carrying context is
      // "branched". Both results name the new session in the same position.
      const sessionId = announcedId(text, ['opened ', 'branched '])
      return {
        target: 'session',
        verb: 'Started',
        label: str(args, 'title') || 'a session',
        ...(sessionId ? { sessionId } : {}),
        ...(firstLine(str(args, 'text'))
          ? { detail: firstLine(str(args, 'text')) }
          : {}),
        ...(pending ? { pending: true } : {}),
      }
    }
    case 'send_message': {
      const sessionId = str(args, 'sessionId')
      return {
        target: 'session',
        verb: 'Messaged',
        label: sessionId || 'a session',
        ...(sessionId ? { sessionId } : {}),
        ...(firstLine(str(args, 'text'))
          ? { detail: firstLine(str(args, 'text')) }
          : {}),
        ...(pending ? { pending: true } : {}),
      }
    }
    case 'create_task': {
      const documentId = announcedId(text, ['Created '])
      return {
        target: 'document',
        verb: 'Created',
        label: str(args, 'title') || 'a task',
        ...(documentId ? { documentId } : {}),
        ...(firstLine(str(args, 'description'))
          ? { detail: firstLine(str(args, 'description')) }
          : {}),
        ...(pending ? { pending: true } : {}),
      }
    }
    case 'update_task':
    case 'complete_task': {
      const documentId = str(args, 'taskId') || str(args, 'task_id')
      return {
        target: 'document',
        verb: name === 'complete_task' ? 'Completed' : 'Updated',
        label: str(args, 'title') || documentId || 'a task',
        ...(documentId ? { documentId } : {}),
        ...(firstLine(str(args, 'note'))
          ? { detail: firstLine(str(args, 'note')) }
          : {}),
        ...(pending ? { pending: true } : {}),
      }
    }
    case 'comment': {
      const action = str(args, 'action')
      // `list` is a read, and `delete` removes the thing the card would point
      // at. Neither is something anybody scrolls back to find.
      if (action !== 'create' && action !== 'reply' && action !== 'resolve') {
        return null
      }
      const target =
        args && typeof args === 'object'
          ? ((args as Record<string, unknown>).target as
              | Record<string, unknown>
              | undefined)
          : undefined
      const documentId = argString(target?.documentId) ?? ''
      const anchor = argString(target?.anchorText) ?? ''
      if (action === 'resolve') {
        return {
          target: 'comment',
          verb: 'Resolved',
          label: str(args, 'id') || 'a comment',
          ...(pending ? { pending: true } : {}),
        }
      }
      const replyTo = action === 'reply' ? str(args, 'id') : ''
      return {
        target: 'comment',
        verb: action === 'reply' ? 'Replied' : 'Commented',
        ...(replyTo ? { replyTo } : {}),
        // The anchor is what the comment is ABOUT, which beats a document id as
        // a label: "about: the second paragraph" tells you where you are.
        label: anchor
          ? `on “${firstLine(anchor, 60)}”`
          : documentId || 'a comment',
        ...(documentId ? { documentId } : {}),
        ...(firstLine(str(args, 'body'))
          ? { detail: firstLine(str(args, 'body')) }
          : {}),
        ...(pending ? { pending: true } : {}),
      }
    }
    default:
      return null
  }
}

/**
 * The card a synthesised system message deserves.
 *
 * These three (RFC 0286/0289/0292) are the interactions that arrive from OUTSIDE
 * the turn: another session finished, somebody answered a comment, somebody
 * named this session. They matter more than anything the agent did on its own,
 * and today they render as grey italic prose written for a model — three
 * paragraphs of "read the whole reply with read_session on that session id",
 * addressed to the agent, shown to the person.
 *
 * The card shows the human half (who, what they said, where) and keeps the
 * instruction out of it. `ref` carries the ids, so nothing here parses the
 * sentence.
 */
export function cardForEvent(message: TranscriptMessage): ThreadCard | null {
  const ref = message.ref
  switch (message.kind) {
    case 'session_reply':
      return {
        target: 'session',
        verb: 'Finished',
        label: 'a session you dispatched',
        ...(ref?.sessionId ? { sessionId: ref.sessionId } : {}),
        ...(ref?.summary ? { detail: firstLine(ref.summary) } : {}),
      }
    case 'session_stopped':
      // RFC 0348 3e: one quiet line per stop.
      return {
        target: 'session',
        verb:
          ref?.status === 'started'
            ? 'Started'
            : ref?.status === 'asking'
              ? 'Asking'
              : ref?.status === 'failed'
                ? 'Failed'
                : ref?.status === 'cancelled'
                  ? 'Stopped'
                  : 'Finished',
        label: 'a session',
        ...(ref?.sessionId ? { sessionId: ref.sessionId } : {}),
        ...(ref?.summary ? { detail: firstLine(ref.summary) } : {}),
      }
    case 'comment_reply':
      return {
        target: 'comment',
        verb: `${ref?.author ?? 'Someone'} replied`,
        label: ref?.anchorText
          ? `on “${firstLine(ref.anchorText, 60)}”`
          : 'to a comment you wrote',
        ...(ref?.documentId ? { documentId: ref.documentId } : {}),
        ...(ref?.summary ? { detail: firstLine(ref.summary) } : {}),
      }
    case 'mentioned':
      return {
        target: 'comment',
        verb: `${ref?.author ?? 'Someone'} named you`,
        label: ref?.anchorText
          ? `on “${firstLine(ref.anchorText, 60)}”`
          : 'in a comment',
        ...(ref?.documentId ? { documentId: ref.documentId } : {}),
        ...(ref?.summary ? { detail: firstLine(ref.summary) } : {}),
      }
    case 'comment_claimed':
      return {
        target: 'comment',
        verb: `${ref?.author ?? 'Someone'} commented`,
        label: ref?.anchorText
          ? `on “${firstLine(ref.anchorText, 60)}”`
          : 'on a document you are paired to',
        ...(ref?.documentId ? { documentId: ref.documentId } : {}),
        ...(ref?.summary ? { detail: firstLine(ref.summary) } : {}),
      }
    case 'undone':
      // RFC 0313 3f: told to this session only, so the person reading its
      // thread sees why the agent will not make that edit again.
      return {
        target: 'document',
        verb: `${ref?.author ?? 'Someone'} undid your change`,
        label: 'to a document',
        ...(ref?.documentId ? { documentId: ref.documentId } : {}),
        ...(ref?.summary ? { detail: firstLine(ref.summary) } : {}),
      }
    default:
      return null
  }
}
